import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Chip, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

const statusOptions = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
];

const summaryCards = [
  { key: "total", label: "Total Tickets", color: "dark" },
  { key: "open", label: "Open", color: "warning" },
  { key: "in_progress", label: "In Progress", color: "info" },
  { key: "resolved", label: "Resolved", color: "success" },
];

const statusLabel = (status) =>
  ({ open: "Open", in_progress: "In Progress", resolved: "Resolved" }[status] || status || "Open");

const statusColor = (status) =>
  ({ open: "warning", in_progress: "info", resolved: "success" }[status] || "default");

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function SupportIssues() {
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [limit, setLimit] = useState(10);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");

  const showNotice = (content, color = "success") => {
    setMessage(content);
    setNotiColor(color);
    setNotification(true);
  };

  const ensureAuth = () => {
    if (init() === "success") return true;
    window.location.assign("/signin");
    return false;
  };

  const loadTickets = async () => {
    if (!ensureAuth()) return;
    setLoading(true);
    try {
      const response = await axios.get(Constants.getUrls.getAllSupportIssues, {
        params: { page, limit, status: statusFilter, topic: topicFilter, search: searchText },
      });
      setTickets(response?.data?.data?.docs || []);
      setSummary(response?.data?.summary || { total: 0, open: 0, in_progress: 0, resolved: 0 });
      setTopics(response?.data?.topics || []);
      setTotalPages(response?.data?.data?.totalPages || 1);
      setTotalDocs(response?.data?.data?.totalDocs || 0);
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to load support tickets.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [page, limit, statusFilter, topicFilter, searchText]);

  const openTicket = (item) => {
    if (!ensureAuth()) return;
    const identifier = item?.ticket_id || item?._id;
    if (!identifier) {
      showNotice("This ticket does not have a valid identifier.", "error");
      return;
    }
    window.open(
      `/support-issues/${encodeURIComponent(identifier)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={2} mb={2}>
          {summaryCards.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.key}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="button" color="text">{item.label}</MDTypography>
                  <MDTypography variant="h4" color={item.color}>{summary[item.key] || 0}</MDTypography>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card>
          <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
            <MDTypography variant="h6" color="white">Support Ticket Management</MDTypography>
          </MDBox>
          <MDBox p={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(event) => { setPage(1); setStatusFilter(event.target.value); }}>
                    {statusOptions.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel>Topic</InputLabel>
                  <Select value={topicFilter} label="Topic" onChange={(event) => { setPage(1); setTopicFilter(event.target.value); }}>
                    <MenuItem value="">All topics</MenuItem>
                    {topics.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Search ticket ID, user, phone or email"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      setPage(1);
                      setSearchText(searchInput.trim());
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <MDButton color="info" fullWidth onClick={() => { setPage(1); setSearchText(searchInput.trim()); }}>
                  Search
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox py={3}>
            {loading ? (
              <Grid align="center"><h4>Loading tickets...</h4></Grid>
            ) : tickets.length ? (
              <DataTable
                table={{
                  columns: [
                    { Header: "ticket", accessor: "ticket" },
                    { Header: "user", accessor: "user" },
                    { Header: "topic", accessor: "topic" },
                    { Header: "raised", accessor: "raised" },
                    { Header: "last activity", accessor: "activity" },
                    { Header: "status", accessor: "status" },
                    { Header: "action", accessor: "action" },
                  ],
                  rows: tickets.map((item) => ({
                    ticket: (
                      <MDBox>
                        <MDTypography variant="button" fontWeight="bold">{item.ticket_id || "Migrating..."}</MDTypography>
                        {item.admin_unread_count > 0 ? <MDTypography variant="caption" color="error" display="block">{item.admin_unread_count} new</MDTypography> : null}
                      </MDBox>
                    ),
                    user: (
                      <MDBox>
                        <MDTypography variant="button" fontWeight="medium">{item.user_id?.name || item.name || "-"}</MDTypography>
                        <MDTypography variant="caption" color="text" display="block">{item.user_id?.mobile || item.mobile || item.user_id?.email || item.email || "-"}</MDTypography>
                      </MDBox>
                    ),
                    topic: item.reason || "-",
                    raised: formatDate(item.createdAt),
                    activity: formatDate(item.last_activity_at || item.updatedAt),
                    status: <Chip size="small" label={statusLabel(item.support_status)} color={statusColor(item.support_status)} variant="outlined" />,
                    action: <MDButton color="info" size="small" onClick={() => openTicket(item)}>Open Reply</MDButton>,
                  })),
                }}
                entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
                showTotalEntries
                page_no={page}
                totalPages={totalPages}
                totalDocs={totalDocs}
                onPageChange={setPage}
                onEntriesPerPageChange={(nextLimit) => { setLimit(nextLimit); setPage(1); }}
              />
            ) : (
              <MDBox px={3}><MDTypography variant="button">No support tickets found.</MDTypography></MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime="Just now"
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
      />
    </DashboardLayout>
  );
}

export default SupportIssues;
